import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Row, Button, Table, Form, Select, message, Tooltip, notification, Spin, Input } from 'antd';
import { PlusOutlined, SettingFilled, AppstoreFilled } from '@ant-design/icons';
import { 
  getDependencies,
  getDependencyByCode,
  getDependencyTypes,
  getDependencyAttributes,
  deleteDependency, 
  createDependency, 
  updateDependency 
} from '../../API/dependencies';
import { getAssets } from '../../API/assets';

import DependencySettings from './components/DependencySettings/DependencySettings';
import DependencySearch from './components/DependencySearch/DependencySearch';
import DependencyModal from './components/DependencyModal/DependencyModal';
import Columns from './components/DependencyTable/columns';

import WithPermission from "../Common/WithPermission/WithPermission";
import ExportButton from '../Common/ExportButton/ExportButton';
import PageTitle from '../Common/PageTitle/PageTitle';
import Assetia from '../Common/Assetia/Assetia';

import { useAuth } from "../Context/AuthContext";

import './Dependencies.css';

const Dependencies = () => {
  // Permisos
  const { hasPermission, userData } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;
  // Consulta permisos: Tiene rol asignado  
  const hasAnyRole = userData?.roles?.length > 0;
  // Consulta especifica de permisos
  const canDependenciesRead = userData ? hasPermission("dependencies", "read") : false;
  const canDependenciesCreate = userData ? hasPermission("dependencies", "create") : false;

  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const [dependencies, setDependencies] = useState([]);
  const [dependencyTypes, setDependencyTypes] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedDependency, setSelectedDependency] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  // Loader
  const [loading, setLoading] = useState(true);
  // Estado de búsqueda global
  //  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  // Capturar activo
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const assetNameFromUrl = searchParams.get('asset');
  const [initialSearchApplied, setInitialSearchApplied] = useState(false);  
  // Obtener los filtros del Dashboard de la URL
  const assetsFromUrl = searchParams.get('assets')?.split(',').filter(Boolean) || [];
  const areasFromUrl = searchParams.get('areas')?.split(',').filter(Boolean) || [];
  const dependenciesFromUrl = searchParams.get('dependencies')?.split(',').filter(Boolean) || [];
  const [activeFilters, setActiveFilters] = useState({});

  // Detectar si los filtros vienen del dashboard
  const hasUrlFilters = assetsFromUrl.length > 0 || areasFromUrl.length > 0 || dependenciesFromUrl.length > 0;

  // Sincronizar filtros del dashboard con activeFilters
  useEffect(() => {
    const initialFilters = {};
    
    if (assetsFromUrl.length > 0) {
      initialFilters.asset = assetsFromUrl;
    }
    
    if (areasFromUrl.length > 0) {
      initialFilters.area = areasFromUrl;
    }

    if (dependenciesFromUrl.length > 0) {
      initialFilters.dependency = dependenciesFromUrl;
    }
    
    // Solo setear si hay filtros del dashboard
    if (Object.keys(initialFilters).length > 0) {
      setActiveFilters(initialFilters);
    }
  }, [assetsFromUrl.join(','), areasFromUrl.join(','), dependenciesFromUrl.join(',')]);

  const fetchDependencies = async () => {
    setLoading(true);
    try {
      const data = await getDependencies();
      setDependencies([...data]);
      setFilteredData(data);
      
      // Aplicar filtro SOLO cuando es la primera carga y hay parámetro en URL
      if (!initialSearchApplied && assetNameFromUrl) {
        const filtered = data.filter(record => 
          record.asset?.name?.toLowerCase().includes(assetNameFromUrl.toLowerCase())
        );
        setFilteredData(filtered);
        //setSearchText(assetNameFromUrl);
        setInitialSearchApplied(true);
      }
    } catch (error) {
      message.error(error.message || 'Error al cargar las dependencias.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDependencyTypes = async () => {
    try {
      const data = await getDependencyTypes();
      setDependencyTypes(data || []);
    } catch (error) {
      message.error(error.message || 'Error al cargar los tipos de dependencia.');
      setDependencyTypes([]);
    }
  };

  const fetchAttributes = async () => {
    try {
      const data = await getDependencyAttributes(); 
      setAttributes(data);
    } catch (error) {
      message.error(error.message || 'Error al cargar los atributos.');
    }
  };

  const fetchAssets = async () => {
    try {
      const data = await getAssets();
      setAssets(data);
    } catch (error) {
      message.error(error.message || 'Error al cargar los activos.');
    }
  };

  useEffect(() => {
    fetchDependencies();
  }, []);

  /* const handleOpenModal = async (dependency = null) => {
    if (dependency) {
      // PUT
      setSelectedDependency(dependency);
      form.setFieldsValue({
        ...dependency,
        type: dependency.type?.id, 
        asset: dependency.asset?.code,
        area_name: dependency.asset?.area?.name || undefined
      });
    } else {
      // CREATE
      setSelectedDependency(null); 
      form.resetFields(); 
    }

    // Traemos datos necesarios
    await fetchDependencyTypes();
    await fetchAttributes();
    await fetchAssets();

    setIsModalVisible(true);
  } */;

  const handleOpenModal = async (dependency = null) => {
    // Traemos datos necesarios primero
    await fetchDependencyTypes();
    await fetchAttributes();
    await fetchAssets();

    setIsModalVisible(true); 

    if (dependency) {
      // PUT - Editar
      setSelectedDependency(dependency);
      
      // Esperamos un tick para asegurar que el form esté listo
      setTimeout(() => {
        form.setFieldsValue({
          name: dependency.name,
          description: dependency.description,
          type: dependency.type?.id,
          asset: dependency.asset?.code,
          area_name: dependency.asset?.area?.name || undefined,
          atributes: dependency.atributes || [{}]
        });
      }, 0);
    } else {
      // CREATE - Nuevo
      setSelectedDependency(null);
      form.resetFields();
      form.setFieldsValue({ 
        atributes: [{}],
        name: undefined,
        description: undefined,
        type: undefined,
        asset: undefined,
        area_name: undefined
      });
    }

    setIsModalVisible(true);
  };
  const handleSave = async (values) => {
    if (!Array.isArray(values.atributes) || values.atributes.length === 0) {
      api.error({
        message: "Error al guardar la dependencia.",
        description: "Debes agregar al menos un atributo antes de guardar.",
        key: 'save-dependency-error'
      });
      return;
    }
    
    try {
      const payload = {
        type_id: values.type,  
        asset_code: values.asset, 
        name: values.name,  
        description: values.description,  
        atributes: values.atributes.map(attr => ({
          atribute: typeof attr.atribute === "object" ? attr.atribute.id : attr.atribute,
          value: attr.value
        })),
        
      };

      if (selectedDependency) {
        await updateDependency(selectedDependency.code, payload);
        api.success({ message: "Dependencia actualizada con éxito."});

      } else {
        await createDependency(payload);
        api.success({ message: "Dependencia creada con éxito."});
      }

      setIsModalVisible(false);
      fetchDependencies();  
    } catch (error) {
      if (error?.response?.data?.name?.[0]?.includes("already exists")) {
      api.error({
        message: "Dependencia duplicada",
        description: "Ya existe una dependencia con este nombre. Por favor, usa un nombre diferente.",
      });
    } else if (error?.message?.includes("atributes") || error?.message?.includes("map")) {
        api.error({
          message: "Error al guardar la dependencia.",
          description: "Debes agregar al menos un atributo antes de guardar."
        });
      } else {
        api.error({
          message: "Error al guardar la dependencia.",
          description: "Por favor, revisá los campos obligatorios.",
          key: 'save-dependency-error'
        });
      }
    }
  };

  const handleDelete = async (code) => {
    try {
      await deleteDependency(code);
      api.success({ message: `Dependencia ${code} eliminada con éxito`});
      fetchDependencies(setDependencies);
    } catch (error) {
      api.error({ message: "Error al eliminar la dependencia"});
    }
  };

  // Filtro columna tipo y activos
  /* const dependencyTableTypes = Array.from(
    new Map(dependencies.map(dep => [dep.type?.id, dep.type]).filter(([id, type]) => id && type)).values()
  ) || []; */

  const dependenciesTableFilter = () => {
    // Función helper para aplicar filtros
    const applyFilter = (data, filterKey, filterValues) => {
      if (!filterValues || filterValues.length === 0) return data;
      
      const filterMap = {
        // Coinciden con dataIndex de las columnas
        'asset': (dep) => dep.asset?.name || '',
        'name': (dep) => dep.name || '', // Nombre de la dependencia
        'asset_area': (dep) => dep.asset?.area?.name || '', // Responsable
        'type': (dep) => dep.type?.name || '',
      };
      
      const getter = filterMap[filterKey];
      if (!getter) return data;
      
      return data.filter(dep => {
        const value = getter(dep);
        return filterValues.includes(value);
      });
    };

    // Obtener datos filtrados excluyendo una columna
    const getFilteredDataForColumn = (excludeColumn) => {
      let data = [...dependencies];
      
      Object.entries(activeFilters).forEach(([key, values]) => {
        if (key !== excludeColumn && values && values.length > 0) {
          data = applyFilter(data, key, values);
        }
      });
      
      return data;
    };

    // Generar opciones dinámicas basadas en filtros activos
    const typesData = getFilteredDataForColumn('type');
    const dependencyTypes = Array.from(
      new Map(typesData.map(d => [d.type?.id, d.type]).filter(([id, type]) => id && type)).values()
    );

    const assetsData = getFilteredDataForColumn('asset');
    const dependencyAssets = Array.from(
      new Map(assetsData.map(d => [d.asset?.code, d.asset]).filter(([code, a]) => code && a)).values()
    );

    const areasData = getFilteredDataForColumn('asset_area'); // Cambiado de 'area' a 'asset_area'
    const dependencyAreas = Array.from(
      new Map(areasData.map(d => [d.asset?.area?.id, d.asset?.area]).filter(([id, area]) => id && area)).values()
    );

    const namesData = getFilteredDataForColumn('name'); // Cambiado de 'dependency' a 'name'
    const dependencyNames = Array.from(
      new Set(namesData.map(d => d.name).filter(Boolean))
    );

    return {
      filterTypes: dependencyTypes,
      filterAssets: dependencyAssets,
      filterAreas: dependencyAreas,
      filterNames: dependencyNames,
      redirectAssetsToDependencies: searchParams.get('asset') || null,
      dashboardAssetsFilter: assetsFromUrl ?? [],
      dashboardAreasFilter: areasFromUrl ?? [],
      dashboardDependenciesFilter: dependenciesFromUrl ?? [],
    };
  };

  const handleTableChange = (pagination, filters) => {
    // Guardar filtros activos
    setActiveFilters(filters);
    
    // Solo modificar URL si hay filtros del dashboard
    if (hasUrlFilters) {
      const newParams = new URLSearchParams();
      
      if (filters.asset?.length > 0) {
        newParams.set('assets', filters.asset.join(','));
      }
      if (filters.area?.length > 0) {
        newParams.set('areas', filters.area.join(','));
      }
      if (filters.dependency?.length > 0) {
        newParams.set('dependencies', filters.dependency.join(','));
      }
      
      // Preservar otros parámetros si existen (como 'asset' de la redirección)
      const currentParams = new URLSearchParams(location.search);
      currentParams.forEach((value, key) => {
        if (!['assets', 'areas', 'dependencies'].includes(key)) {
          newParams.set(key, value);
        }
      });
      
      navigate({
        pathname: '/dependencies',
        search: newParams.toString() ? `?${newParams.toString()}` : ''
      }, { replace: true });
    }
    // Si NO hay filtros del dashboard, NO tocar la URL
  };

  // Autofocus
  const inputRef = useRef(null);

  useEffect(() => {
    if (isModalVisible && !selectedDependency) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }
  }, [isModalVisible, selectedDependency]);

  const dashboardGeneral = JSON.parse(
    localStorage.getItem("dashboardGeneral") || "{}"
  );

  const hasDependencies = dashboardGeneral?.dependencies?.count > 0;

  const canAccessDependencies =
    (hasAnyRole && canDependenciesRead) ||
    hasDependencies;
  return (
    <WithPermission
      isSuperuser={isSuperuser}
      hasAnyRole={hasAnyRole}
      requiredPermission="read"
      currentPermission={canAccessDependencies}
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <div>
          <div className="button-container">
            <Row justify="start">
              <Col span={23}>
                <PageTitle title="Inventario de Dependencias" icon="dependencies" />
              </Col>
            </Row>

            <Row justify="start" gutter={[0, 16]}>
              <Col span={16}>
                <Button
                  className="add-button"
                  onClick={() => handleOpenModal(null)}
                  disabled={!isSuperuser && (!hasAnyRole || !canDependenciesCreate)}
                >
                  <PlusOutlined />
                  Agregar Dependencia
                </Button>
              </Col>
              <Col span={8} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <DependencySearch
                  dependencies={dependencies}
                  //searchText={searchText}
                  //setSearchText={setSearchText}
                  setFilteredData={setFilteredData}
                />
              </Col>
            </Row>

            <Row justify="end" gutter={[8, 0]}>
              {
                isSuperuser &&
                <Col>
                  <Button 
                    type="primary"
                    className="view-deleted-button"
                    onClick={() => {
                      const params = new URLSearchParams();
                      if (assetsFromUrl.length) params.set('assets', assetsFromUrl.join(','));
                      if (areasFromUrl.length) params.set('areas', areasFromUrl.join(','));
                      navigate(`/dependencies-deleted${params.toString() ? `?${params.toString()}` : ''}`);
                    }}>
                    <AppstoreFilled />
                    Ver Borrados
                  </Button>
                </Col>
              }
              <Col>
                <ExportButton
                  api={api}
                  contextHolder={contextHolder}
                  data={filteredData}
                  filename="Dependencias.csv"
                  transformData={(data) =>
                    data.map(item => ({
                      Activos: item.asset?.name || 'N/A',
                      Nombre: item.name,
                      Descripción: item.description,
                      Atributos: Array.isArray(item.atributes) && item.atributes.length > 0
                        ? item.atributes
                            .map(attr =>
                              attr?.atribute?.key
                                ? `${attr.atribute.key}: ${attr.value ?? ''}`
                                : null
                            )
                            .filter(Boolean)
                            .join(', ') || 'N/A'
                        : 'N/A',
                      Tipo: item.type?.name || 'N/A',
                      Responsable: item.asset?.area?.name || 'N/A'
                    }))
                  }
                />
              </Col>
            </Row>
          </div>

          <Spin className="custom-spin" spinning={loading}>
            <Table
              className="custom-pagination"
              columns={Columns(hasPermission, handleOpenModal, handleDelete, dependenciesTableFilter, isSuperuser, hasAnyRole)} 
              dataSource={filteredData}
              scroll={{ y: 450 }}
              rowKey="code"
              pagination={{
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ['30', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`
              }}
              /* onChange={(pagination, filters) => {
                if (filters.type) {
                  setFilteredData(dependencies.filter(record => filters.type.includes(record.type?.name)));
                } else {
                  setFilteredData(dependencies);
                }
              }} */
             onChange={handleTableChange}
            />
          </Spin>
          
          <Row style={{ marginTop: 80 }}>
            <Assetia
              pageTitle="dependencias"
              pageDescription="cargar tus dependencias y gestionarlas de manera simple"
              view="dependencies"
            />
          </Row>

          {/* Modal Agregar/Editar Dependencia */}
          <DependencyModal
            isVisible={isModalVisible}
            onClose={() => setIsModalVisible(false)}
            onSave={handleSave}
            form={form}
            selectedDependency={selectedDependency}
            dependencyTypes={dependencyTypes}
            assets={assets}
            attributes={attributes}
            inputRef={inputRef}
            api={api}
          />

          {/* Modal Configuraciones de Dependencia */}
          {/* <DependencySettings
            isVisible={isSettingsModalVisible}
            onClose={() => setIsSettingsModalVisible(false)}
            dependencyTypes={dependencyTypes}
            attributes={attributes}
          /> */}
        </div>
      </>
    </WithPermission>
  );
};

export default Dependencies;